import { z } from 'zod';
import {
  ATTACHMENT_VARIANT_NAMES,
  FILE_PREVIEW_KINDS,
  FILE_VARIANT_NAMES,
  SNIPPET_LANGUAGES,
  SNIPPET_LIMITS,
} from '../enums';
import { isoDateTimeSchema, singleLineText, uuidSchema } from './common';
import { paginated, paginationQuerySchema } from './pagination';

/**
 * Files contracts (PLAN §4 "Files (Phase 1)", D-010, D-111). Uploads go straight to object
 * storage through presigned URLs — one PUT up to 100 MiB, S3 multipart with presigned part URLs
 * above — and only metadata passes through the API. The local storage driver emulates both for
 * development and tests. Owned by the files workstream from Phase 1 on; the Phase 0 avatar and
 * local-file contracts stay in `org.ts`.
 */

/** Single PUT up to this size; multipart above it (D-010). */
export const SINGLE_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;
/** Multipart part size (the last part may be smaller). */
export const MULTIPART_PART_BYTES = 16 * 1024 * 1024;
/** Most part URLs presigned in one call. */
export const MAX_PARTS_PER_PRESIGN = 100;
/** S3's limit on parts per multipart upload. */
export const MAX_MULTIPART_PARTS = 10_000;
/** Upload inits per user per 10 minutes (`429 upload_quota_exceeded`). */
export const UPLOAD_INITS_PER_10_MIN = 60;
/** Bytes a user may start uploading per rolling 24 hours (`429 upload_quota_exceeded`). */
export const UPLOAD_DAILY_QUOTA_BYTES = 20 * 1024 * 1024 * 1024;

export const filePreviewKindSchema = z.enum(FILE_PREVIEW_KINDS);
export const attachmentVariantSchema = z.enum(ATTACHMENT_VARIANT_NAMES);

/**
 * Statuses a client can see: `files.status`, plus `removed` for a soft-deleted file that a message
 * still references (the message shows "file removed"; name and size stay, nothing can be signed).
 */
export const fileStatusDtoSchema = z.enum([
  'pending_upload',
  'processing',
  'ready',
  'quarantined',
  'failed',
  'removed',
]);

/**
 * `FileDto`: never carries a URL. It carries the file's identity and shape, and a client that
 * wants the bytes asks `GET /files/:id/content` for them (D-1104).
 */
export const snippetLanguageSchema = z.enum(SNIPPET_LANGUAGES);

/**
 * What a snippet is, beside being a file (D-1261): the language it is highlighted in, how big it
 * is, and enough of its first lines to draw the card in the conversation without fetching the
 * bytes. `preview` is already cut to {@link SNIPPET_LIMITS.previewLines} lines and
 * {@link SNIPPET_LIMITS.previewChars} characters; `hasMore` says whether "Show more" has anything
 * to show.
 */
export const snippetDtoSchema = z.object({
  language: snippetLanguageSchema,
  lineCount: z.number().int().nonnegative(),
  charCount: z.number().int().nonnegative(),
  preview: z.string(),
  hasMore: z.boolean(),
});
export type SnippetDto = z.infer<typeof snippetDtoSchema>;

export const fileDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** Sniffed server-side after upload; the client's value is only a hint. */
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  status: fileStatusDtoSchema,
  previewKind: filePreviewKindSchema,
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  durationSec: z.number().nullable(),
  pageCount: z.number().int().nullable(),
  variants: z.array(attachmentVariantSchema),
  /** Set only for a text snippet (D-1261); null for every other file. */
  snippet: snippetDtoSchema.nullable(),
});
export type FileDto = z.infer<typeof fileDtoSchema>;

export const fileIdParamsSchema = z.object({ fileId: uuidSchema });
export type FileIdParams = z.infer<typeof fileIdParamsSchema>;

/** GET /files/:fileId — file-read. */
export const fileDetailDtoSchema = fileDtoSchema.extend({
  uploadedBy: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema,
  /** Hex SHA-256 once the `media` job computed it (null while pending or processing). */
  sha256: z.string().nullable(),
});
export type FileDetailDto = z.infer<typeof fileDetailDtoSchema>;

/** Purposes a client may start an upload for (`files.purpose`). */
export const UPLOAD_PURPOSES = [
  'attachment',
  'task_attachment',
  'evidence',
  'transcript',
  'training_content',
  'slack_import_archive',
  'ptstdm_import',
] as const;
export const uploadPurposeSchema = z.enum(UPLOAD_PURPOSES);
export type UploadPurpose = z.infer<typeof uploadPurposeSchema>;

/**
 * POST /files/uploads — any member (60 per 10 min, 20 GiB/day). `Idempotency-Key` optional.
 * Phase 1 accepts `purpose: attachment` only; the other purposes open with their phases
 * (`400 validation_failed`, `details.reason = purpose_not_available`).
 */
export const createUploadRequestSchema = z.strictObject({
  fileName: singleLineText(1, 255),
  mimeType: z.string().trim().min(1).max(255),
  /** ≤ `messaging.maxUploadBytes` for attachments (`413 file_too_large`). */
  sizeBytes: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  /** Optional client-computed hex SHA-256 (checked by the media job when present). */
  sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/i)
    .optional(),
  purpose: uploadPurposeSchema,
  /** Where the file will be shared (sets `files.origin_conversation_id`); the caller must be able to post there. */
  conversationId: uuidSchema.optional(),
});
export type CreateUploadRequest = z.infer<typeof createUploadRequestSchema>;

export const singleUploadSchema = z.object({
  kind: z.literal('single'),
  method: z.literal('PUT'),
  /** Presigned (S3) or HMAC-signed (local driver) URL. */
  url: z.string(),
  /** Headers the PUT must send exactly (`content-type`: it is part of the signature). */
  headers: z.record(z.string(), z.string()),
  expiresAt: isoDateTimeSchema,
});

export const multipartUploadSchema = z.object({
  kind: z.literal('multipart'),
  uploadId: z.string(),
  partSizeBytes: z.number().int().positive(),
  partCount: z.number().int().positive(),
});

export const uploadInstructionsSchema = z.discriminatedUnion('kind', [
  singleUploadSchema,
  multipartUploadSchema,
]);
export type UploadInstructions = z.infer<typeof uploadInstructionsSchema>;

export const createUploadResponseSchema = z.object({
  /** `status = pending_upload`. */
  file: fileDtoSchema,
  upload: uploadInstructionsSchema,
});
export type CreateUploadResponse = z.infer<typeof createUploadResponseSchema>;

/** POST /files/:fileId/upload-parts — uploader. */
export const uploadPartsRequestSchema = z.strictObject({
  partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(MAX_PARTS_PER_PRESIGN),
});
export type UploadPartsRequest = z.infer<typeof uploadPartsRequestSchema>;

export const uploadPartsResponseSchema = z.object({
  parts: z.array(
    z.object({ partNumber: z.number().int(), url: z.string(), expiresAt: isoDateTimeSchema }),
  ),
});
export type UploadPartsResponse = z.infer<typeof uploadPartsResponseSchema>;

/**
 * PUT /files/local/* — the local storage driver's signed upload URLs (development and tests):
 * a single PUT (`exp`, `sig`; the `content-type` header is signed) or one multipart part
 * (`uploadId`, `partNumber` too). The response carries the object's or part's `ETag` header, like
 * S3. Errors: `403 signature_invalid`, `410 url_expired`, `409 file_not_uploading`,
 * `413 file_too_large` (more bytes than declared, or than the part size).
 */
export const localFileUploadQuerySchema = z.object({
  exp: z.coerce.number().int().positive(),
  sig: z.string().regex(/^[0-9a-f]{64}$/),
  uploadId: z
    .string()
    .regex(/^[0-9a-f]{32}$/)
    .optional(),
  partNumber: z.coerce.number().int().min(1).max(MAX_MULTIPART_PARTS).optional(),
});
export type LocalFileUploadQuery = z.infer<typeof localFileUploadQuerySchema>;

/** POST /files/:fileId/complete — uploader. `parts` is required for multipart uploads. */
export const completeUploadRequestSchema = z.strictObject({
  parts: z
    .array(
      z.strictObject({
        partNumber: z.number().int().min(1).max(10_000),
        etag: z.string().min(1).max(200),
      }),
    )
    .min(1)
    .max(10_000)
    .optional(),
});
export type CompleteUploadRequest = z.infer<typeof completeUploadRequestSchema>;

/** Variants a download URL can be signed for. */
export const downloadVariantSchema = z.enum(['original', ...ATTACHMENT_VARIANT_NAMES]);
export type DownloadVariant = z.infer<typeof downloadVariantSchema>;

/**
 * Any stored rendering of a file, which is wider than {@link downloadVariantSchema}: that one is
 * the set an *attachment* has, and the content route also serves the three `avatar_*` sizes
 * (D-1105). What exists for a given file is decided by its own `variants`, not by this list — an
 * unknown-but-valid name simply 404s.
 */
export const fileContentVariantSchema = z.enum(['original', ...FILE_VARIANT_NAMES]);
export type FileContentVariant = z.infer<typeof fileContentVariantSchema>;

/**
 * GET /files/:fileId/content — file-read, re-checked on this request (D-1104). `inline` is
 * honoured only for the preview allowlist (images, video, audio, PDF, plain text); everything
 * else is served as an attachment whatever the caller asks for.
 */
export const fileContentQuerySchema = z.object({
  variant: fileContentVariantSchema.default('original'),
  disposition: z.enum(['inline', 'attachment']).default('attachment'),
});
export type FileContentQuery = z.infer<typeof fileContentQuerySchema>;

/**
 * A short-lived link to an object in the store. Only two endpoints still issue one — the training
 * certificate and the workspace export (D-1106) — and both are whole-file downloads behind their
 * own check. File *content* is streamed instead: see `fileContentQuerySchema`.
 */
export const signedUrlSchema = z.object({ url: z.string(), expiresAt: isoDateTimeSchema });
export type SignedUrl = z.infer<typeof signedUrlSchema>;

/** GET /conversations/:conversationId/files — the Files tab (conv-read). */
export const conversationFilesQuerySchema = paginationQuerySchema.extend({
  kind: z.enum(['image', 'video', 'pdf', 'other']).optional(),
});
export type ConversationFilesQuery = z.infer<typeof conversationFilesQuerySchema>;

export const conversationFileItemSchema = z.object({
  file: fileDtoSchema,
  messageId: uuidSchema,
  sharedAt: isoDateTimeSchema,
  sharedBy: uuidSchema,
});
export const conversationFilesResponseSchema = paginated(conversationFileItemSchema);
export type ConversationFilesResponse = z.infer<typeof conversationFilesResponseSchema>;

/**
 * POST /files/snippets (D-1261) — any member, the same rate limit as starting an upload. The
 * server writes the text to the store itself, so there is no presigned round trip: one call makes
 * a `ready` file the caller can attach to the message they are writing.
 */
export const createSnippetRequestSchema = z.strictObject({
  /** Shown on the card and used as the file name; blank takes the first line of the content. */
  title: z.string().trim().max(SNIPPET_LIMITS.maxTitleChars).default(''),
  language: snippetLanguageSchema.default('plaintext'),
  content: z.string().min(1).max(SNIPPET_LIMITS.maxContentChars),
  /** Where it will be shared; the caller must be able to post there. */
  conversationId: uuidSchema.optional(),
});
export type CreateSnippetRequest = z.infer<typeof createSnippetRequestSchema>;

/**
 * GET /files/:fileId/snippet (D-1261) — file-read, the same rule as the bytes. The whole text up
 * to {@link SNIPPET_LIMITS.maxInlineChars}; beyond that `truncated` is true and the rest is the
 * download.
 */
export const snippetContentResponseSchema = z.object({
  fileId: uuidSchema,
  title: z.string(),
  language: snippetLanguageSchema,
  content: z.string(),
  truncated: z.boolean(),
  lineCount: z.number().int().nonnegative(),
  charCount: z.number().int().nonnegative(),
});
export type SnippetContentResponse = z.infer<typeof snippetContentResponseSchema>;
